
import {
  defineComponent, onMounted, PropType, Ref, ref, watch,
} from 'vue';
import { VCodeMirror } from 'vue3-code-mirror';
import { useI18n } from 'vue-i18n';
import { FieldConfig, ParserFieldObject, Property } from '@/types';

import 'codemirror/mode/htmlmixed/htmlmixed';
import beautifier from 'js-beautify';
import VariablesButton from '@/components/fields/partials/VariablesButton.vue';
import CodeMirrorModal from '@/components/common/CodeMirrorModal.vue';

export default defineComponent({
  name: 'Code',
  components: {
    VariablesButton,
    VCodeMirror,
    CodeMirrorModal,
  },

  props: {
    configs: {
      type: Object as PropType<FieldConfig>,
      required: true,
    },

    parserValues: {
      type: Object as PropType<ParserFieldObject>,
      required: true,
    },
  },

  emits: {
    'on-change-properties': Object,
  },

  setup(props, context) {
    const { t } = useI18n();
    const properties: Ref<Array<Property>> = ref(props.parserValues.properties ?? []);
    const inputValue: Ref<string> = ref(properties.value[0].value);
    const cursorPosition = ref({ line: 0, ch: 0 });
    const codeMirrorEditorRef = ref();
    const showCodeModal = ref(false);
    let lastScrollPosition = { top: 0, left: 0 };

    const restoreScroll = () => {
      if (codeMirrorEditorRef.value && codeMirrorEditorRef.value?.editor) {
        setTimeout(() => {
          codeMirrorEditorRef.value.editor.scrollTo(lastScrollPosition.left, lastScrollPosition.top);
        }, 200);
      }
    };

    const handleValueChange = () => {
      const updatedProperties: Array<Property> = properties.value.map((prop: Property) => {
        const updatedProperty = prop;
        updatedProperty.value = inputValue.value;
        return updatedProperty;
      });

      context.emit('on-change-properties', {
        selector: props.parserValues.selector,
        properties: updatedProperties,
      });
    };

    const insertContent = (value: string|number|undefined) => {
      if (codeMirrorEditorRef.value) {
        codeMirrorEditorRef.value.editor.getDoc().replaceRange(value, cursorPosition.value);
        handleValueChange();
      }
    };

    const onBlur = (editor: Record<string, Function>) => {
      lastScrollPosition = { top: editor.getScrollInfo().top, left: editor.getScrollInfo().left };
      cursorPosition.value = {
        line: editor.getCursor().line,
        ch: editor.getCursor().ch,
      };

      const currentValue = inputValue.value;
      let newValue;
      properties.value.map((prop: Property) => {
        newValue = prop.value;
        return prop;
      });

      if (currentValue !== newValue) {
        handleValueChange();
      }
    };

    onMounted(() => {
      if (codeMirrorEditorRef.value) {
        lastScrollPosition = { top: 0, left: 0 };
        codeMirrorEditorRef.value.editor.setOption('theme', 'default');
      }
    });

    watch(() => props.parserValues.properties, (newProperties, oldProperties) => {
      if (newProperties && newProperties[0] && newProperties[0].value !== inputValue.value) {
        inputValue.value = beautifier.html(newProperties[0].value);
        restoreScroll();
      }
    }, { deep: true, immediate: true });

    // Also watch the specific value for additional safety
    watch(() => properties.value[0].value, (values, oldValues) => {
      if (values !== oldValues && values !== inputValue.value) {
        inputValue.value = beautifier.html(values);
        restoreScroll();
      }
    }, { immediate: true });

    const openCodeModal = () => {
      showCodeModal.value = true;
    };

    const handleModalApply = (code: string) => {
      inputValue.value = code;
      handleValueChange();
    };

    return {
      t,
      inputValue,
      codeMirrorEditorRef,
      insertContent,
      onBlur,
      handleValueChange,
      showCodeModal,
      openCodeModal,
      handleModalApply,
    };
  },
});
